import { useEffect, useRef, useState } from 'react';

/**
 * Object URLs for a list of blobs, in the order given: something an `<img>`
 * can display, and null where there is no blob yet. The request of 5 October
 * 2026 (third), section 6.
 *
 * For the image viewer, which has to own what it shows: a tile revokes its
 * object URL when it leaves the screen, and the viewer stays open while the
 * list under it refreshes.
 *
 * Made while rendering and not in an effect, so a blob that is already here is
 * drawn in the first frame, which is the frame a photograph grows out of its
 * tile in. One URL per blob, so the same blob twice in the list answers the
 * same URL. Revoked once its blob is no longer in the list, and all of them
 * when the caller goes.
 */
export function useObjectUrls(blobs: readonly (Blob | null | undefined)[]): (string | null)[] {
  const made = useRef(new Map<Blob, string>());
  const [, redraw] = useState(0);

  const objectUrls = blobs.map((blob) => {
    if (blob === null || blob === undefined) return null;
    let objectUrl = made.current.get(blob);
    if (objectUrl === undefined) {
      objectUrl = URL.createObjectURL(blob);
      made.current.set(blob, objectUrl);
    }
    return objectUrl;
  });

  // After every render: whatever was made for a blob no longer in the list goes.
  useEffect(() => {
    const wanted = new Set<Blob>();
    for (const blob of blobs) if (blob !== null && blob !== undefined) wanted.add(blob);
    for (const [blob, objectUrl] of made.current) {
      if (wanted.has(blob)) continue;
      URL.revokeObjectURL(objectUrl);
      made.current.delete(blob);
    }
  });

  useEffect(() => {
    const held = made.current;
    /*
     * A development build mounts every component twice, and the unmount in
     * between has revoked what the first render made. Drawn once more, so
     * nothing on the screen points at a revoked URL.
     */
    if (blobs.some((blob) => blob !== null && blob !== undefined && !held.has(blob))) {
      redraw((count) => count + 1);
    }

    return () => {
      for (const objectUrl of held.values()) URL.revokeObjectURL(objectUrl);
      held.clear();
    };
    // Once, for the life of the caller: the effect above keeps the list.
  }, []);

  return objectUrls;
}
